import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { PageHeader } from "@/components/shared";
import { Mail, MailCheck, Save, SlidersHorizontal, FileCode2, Send } from "lucide-react";

// Demo values mirror backend EMAIL_DEMO_VALUES for the live preview.
const DEMO = {
  asset_name: "Furgone Fiat Ducato", activity: "Revisione", due_date: "15-07-2026",
  employee_name: "Mario Rossi", km_info: " (a 120.000 km)",
  asset_list: "Furgone Fiat Ducato, Trapano Bosch × 2", assigned_date: "01-06-2026 09:30",
  assigned_by: "Amministratore", notes: "Consegna standard di cantiere",
  returned_date: "20-06-2026 17:00", operator_name: "Luca Bianchi",
};

const renderTpl = (tpl, values) =>
  (tpl || "").replace(/{{\s*(\w+)\s*}}/g, (m, k) => (k in values ? values[k] : m));

const SECURITY_OPTIONS = [
  { value: "starttls", label: "STARTTLS (porta 587)" },
  { value: "ssl", label: "SSL/TLS (porta 465)" },
  { value: "none", label: "Nessuna (porta 25)" },
];

export default function Settings() {
  const [smtp, setSmtp] = useState(null);
  const [events, setEvents] = useState([]);
  const [password, setPassword] = useState("");
  const [testTo, setTestTo] = useState("");
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => {
    api.get("/email/config").then(({ data }) => {
      setSmtp(data.smtp);
      setEvents(data.events);
    });
  };
  useEffect(load, []);

  const setSmtpField = (k, v) => setSmtp((s) => ({ ...s, [k]: v }));
  const setEventField = (key, k, v) =>
    setEvents((list) => list.map((e) => (e.key === key ? { ...e, [k]: v } : e)));

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const payload = {
        enabled: !!smtp.enabled,
        host: smtp.host || "",
        port: Number(smtp.port) || 587,
        security: smtp.security || "starttls",
        username: smtp.username || "",
        from_email: smtp.from_email || "",
        from_name: smtp.from_name || "",
        default_recipient: smtp.default_recipient || "",
        notify_days: Number(smtp.notify_days) || 30,
      };
      if (password) payload.password = password;
      await api.put("/email/smtp", payload);
      setPassword("");
      toast.success("Configurazione SMTP salvata");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingSmtp(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Inserisci l'email destinatario del test");
    setTesting(true);
    try {
      const { data } = await api.post("/email/smtp/test", { subject: testTo.trim() });
      toast.success(`Email di prova inviata (${data.via === "smtp" ? "SMTP" : "Resend"})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      const rules = {};
      events.forEach((e) => {
        rules[e.key] = { active: !!e.active, recipients: e.recipients || "" };
      });
      await api.put("/email/rules", { rules });
      toast.success("Regole di notifica salvate");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingRules(false);
    }
  };

  const saveTemplates = async () => {
    setSavingTpl(true);
    try {
      const templates = {};
      events.forEach((e) => {
        templates[e.key] = { subject: e.subject || "", body_html: e.body_html || "" };
      });
      await api.put("/email/templates", { templates });
      toast.success("Template email salvati");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingTpl(false);
    }
  };

  if (!smtp) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Configurazione"
        title="Impostazioni"
        subtitle="Server SMTP, regole di notifica e template email"
      />

      {/* --- SMTP --- */}
      <Card className="p-6 rounded-lg max-w-4xl" data-testid="smtp-card">
        <div className="flex items-start justify-between gap-4 pb-5 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg">Notifiche Email (SMTP)</div>
              <p className="text-sm text-muted-foreground">
                Configura il server SMTP per l'invio automatico delle notifiche. Se non
                configurato, il sistema usa Resend come fallback.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Switch
              checked={smtp.enabled}
              onCheckedChange={(v) => setSmtpField("enabled", v)}
              data-testid="smtp-enabled-switch"
            />
            <span className="text-xs text-muted-foreground">Notifiche email attive</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 py-6">
          <div className="space-y-1.5">
            <Label>Server SMTP (host)</Label>
            <Input
              placeholder="es. smtp.zoho.eu"
              value={smtp.host || ""}
              onChange={(e) => setSmtpField("host", e.target.value)}
              data-testid="smtp-host-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Porta</Label>
            <Input
              type="number"
              value={smtp.port || 587}
              onChange={(e) => setSmtpField("port", e.target.value)}
              data-testid="smtp-port-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Sicurezza</Label>
            <Select value={smtp.security} onValueChange={(v) => setSmtpField("security", v)}>
              <SelectTrigger data-testid="smtp-security-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SECURITY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`smtp-security-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Email destinatario di default</Label>
            <Input
              placeholder="Usato quando una regola non ha destinatari"
              value={smtp.default_recipient || ""}
              onChange={(e) => setSmtpField("default_recipient", e.target.value)}
              data-testid="smtp-default-recipient-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Username</Label>
            <Input
              value={smtp.username || ""}
              onChange={(e) => setSmtpField("username", e.target.value)}
              data-testid="smtp-username-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Password {smtp.has_password && <span className="text-xs text-muted-foreground">(lascia vuoto per non modificare)</span>}</Label>
            <Input
              type="password"
              placeholder={smtp.has_password ? "••••••••" : "Password SMTP"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="smtp-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Mittente (from email)</Label>
            <Input
              value={smtp.from_email || ""}
              onChange={(e) => setSmtpField("from_email", e.target.value)}
              data-testid="smtp-from-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Nome mittente</Label>
            <Input
              value={smtp.from_name || ""}
              onChange={(e) => setSmtpField("from_name", e.target.value)}
              data-testid="smtp-from-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Giorni di anticipo (scadenze)</Label>
            <Input
              type="number"
              min="1"
              value={smtp.notify_days || 30}
              onChange={(e) => setSmtpField("notify_days", e.target.value)}
              data-testid="smtp-notify-days-input"
            />
            <p className="text-xs text-muted-foreground">Quanti giorni prima della scadenza inviare l'avviso.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-border">
          <Button onClick={saveSmtp} disabled={savingSmtp} data-testid="save-smtp-button">
            <Save className="h-4 w-4 mr-1.5" /> {savingSmtp ? "Salvataggio…" : "Salva impostazioni"}
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <Input
              type="email"
              placeholder="Email destinatario del test"
              className="w-64"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              data-testid="smtp-test-email-input"
            />
            <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="send-test-button">
              <MailCheck className="h-4 w-4 mr-1.5" /> {testing ? "Invio…" : "Invia email di prova"}
            </Button>
          </div>
        </div>
      </Card>

      {/* --- Rules --- */}
      <Card className="p-6 rounded-lg max-w-4xl" data-testid="rules-card">
        <div className="flex items-start gap-3 pb-5 border-b border-border">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
            <SlidersHorizontal className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg">Regole notifiche per evento</div>
            <p className="text-sm text-muted-foreground">
              Per ogni evento decidi se inviare l'email e a chi. Più indirizzi separati da virgola.
              Il token <code className="text-primary font-mono">__assignee__</code> viene sostituito con
              l'email dell'assegnatario del bene.
            </p>
          </div>
        </div>

        <div className="py-4 space-y-2">
          <div className="grid grid-cols-[70px_1fr_1.4fr] gap-3 px-2 text-xs font-bold uppercase text-muted-foreground">
            <div>Attiva</div>
            <div>Evento</div>
            <div>Destinatari (separati da virgola)</div>
          </div>
          {events.map((e) => (
            <div
              key={e.key}
              className="grid grid-cols-[70px_1fr_1.4fr] gap-3 items-center px-2 py-2.5 rounded-md hover:bg-muted/40"
              data-testid={`rule-row-${e.key}`}
            >
              <Checkbox
                checked={!!e.active}
                onCheckedChange={(v) => setEventField(e.key, "active", !!v)}
                data-testid={`rule-active-${e.key}`}
              />
              <div>
                <div className="font-medium text-sm">{e.label}</div>
                <div className="text-xs text-muted-foreground font-mono">{e.key}</div>
              </div>
              <Input
                placeholder="__assignee__, ufficio@powercom.srl"
                value={e.recipients || ""}
                onChange={(ev) => setEventField(e.key, "recipients", ev.target.value)}
                data-testid={`rule-recipients-${e.key}`}
              />
            </div>
          ))}
        </div>

        <div className="pt-4 border-t border-border">
          <Button onClick={saveRules} disabled={savingRules} data-testid="save-rules-button">
            <Save className="h-4 w-4 mr-1.5" /> {savingRules ? "Salvataggio…" : "Salva regole"}
          </Button>
        </div>
      </Card>

      {/* --- Templates --- */}
      <Card className="p-6 rounded-lg max-w-4xl" data-testid="templates-card">
        <div className="flex items-start gap-3 pb-5 border-b border-border">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
            <FileCode2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg">Template email</div>
            <p className="text-sm text-muted-foreground">
              Personalizza oggetto e corpo per ogni evento. Usa{" "}
              <code className="text-primary font-mono">{"{{variabile}}"}</code> per inserire i valori
              dinamici — ogni evento espone variabili diverse.
            </p>
          </div>
        </div>

        <Accordion type="multiple" className="py-2">
          {events.map((e) => (
            <AccordionItem key={e.key} value={e.key} data-testid={`tpl-item-${e.key}`}>
              <AccordionTrigger data-testid={`tpl-trigger-${e.key}`}>
                <div className="text-left">
                  <div className="font-medium text-sm">{e.label}</div>
                  <div className="text-xs text-muted-foreground font-mono">{e.key}</div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-1">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wide">Oggetto</Label>
                    <Input
                      value={e.subject || ""}
                      onChange={(ev) => setEventField(e.key, "subject", ev.target.value)}
                      data-testid={`tpl-subject-${e.key}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wide">Corpo (HTML)</Label>
                    <Textarea
                      rows={7}
                      className="font-mono text-xs"
                      value={e.body_html || ""}
                      onChange={(ev) => setEventField(e.key, "body_html", ev.target.value)}
                      data-testid={`tpl-body-${e.key}`}
                    />
                  </div>
                  <div className="rounded-md border border-dashed border-border bg-muted/30 p-2">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                      Variabili disponibili
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {e.variables.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() =>
                            setEventField(e.key, "body_html", `${e.body_html || ""}{{${v}}}`)
                          }
                          className="text-xs font-mono px-2 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20"
                          data-testid={`tpl-var-${e.key}-${v}`}
                        >
                          {`{{${v}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
                      Anteprima live · valori demo per i placeholder
                    </div>
                    <div className="rounded-md border border-border overflow-hidden" data-testid={`tpl-preview-${e.key}`}>
                      <div className="bg-muted px-3 py-2 text-sm font-semibold border-b border-border">
                        {renderTpl(e.subject, DEMO) || "(oggetto vuoto)"}
                      </div>
                      <div
                        className="p-3 text-sm bg-white text-[#374151]"
                        dangerouslySetInnerHTML={{ __html: renderTpl(e.body_html, DEMO) }}
                      />
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="pt-4 border-t border-border">
          <Button onClick={saveTemplates} disabled={savingTpl} data-testid="save-templates-button">
            <Send className="h-4 w-4 mr-1.5" /> {savingTpl ? "Salvataggio…" : "Salva template"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
